import { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  MEMBERSHIP_STATUS,
  MembershipType,
  PROMO_MEMBER_APPLICATION_FLOW,
} from 'types'
import { compose } from 'recompose'
import { connect, useDispatch } from 'react-redux'
import { change } from 'redux-form'
import { useHistory } from 'react-router-dom'
import { flashErrorMessage } from 'redux-flash'
import { selectors as memberSelectors } from 'member-reducer'
import * as memberActions from 'member-actions'
import * as apiActions from 'api-actions'
import {
  apiErrorToErrorMessage,
  processPromoCodeResponse,
  shouldRevalidatePromoCode,
  useTrackingParams,
  useReferralEnabled,
} from 'utils'

const NEW_MEMBER = 'New Member'
const INVALID_CARD_TYPE = 'INVALID_CARD_TYPE'
const INVALID_FLOW_TYPE = 'INVALID_FLOW_TYPE'

const validateDiscountCode = ({
  discountCodeDetails,
  membershipStatus,
  isRenewal,
  membershipType,
  selectedMembershipType,
}) => {
  const { valid_on_business_type, valid_on_membership } = discountCodeDetails

  const validBusinessTypes =
    valid_on_business_type?.split(';').map((type) => type.toLowerCase()) || []

  const validMembershipTypes =
    valid_on_membership?.split(';').map((type) => type.toLowerCase()) || []

  const currentBusinessType =
    membershipStatus === MEMBERSHIP_STATUS.EXPIRED
      ? PROMO_MEMBER_APPLICATION_FLOW.REACQUIRED
      : isRenewal
        ? PROMO_MEMBER_APPLICATION_FLOW.RENEW
        : PROMO_MEMBER_APPLICATION_FLOW.NEW

  const errors = []

  if (
    !!validBusinessTypes.length &&
    !validBusinessTypes.includes(currentBusinessType.toLowerCase())
  ) {
    errors.push(
      `INVALID_CODE_ON_${currentBusinessType.toUpperCase()}_BUSINESS_TYPE`,
    )
  }

  const membershipTypeToValidate =
    membershipStatus !== NEW_MEMBER ? membershipType : selectedMembershipType

  if (
    !!validMembershipTypes.length &&
    !validMembershipTypes.includes(membershipTypeToValidate.toLowerCase())
  ) {
    errors.push('INVALID_CODE_ON_MEMBERSHIP_TYPE')
  }

  if (errors.length > 0) {
    const error = new Error('Discount code validation failed')
    error.errors = { _error: errors }
    throw error
  }
}

const propTypes = {
  children: PropTypes.node.isRequired,
  clearDiscountCodeStateAndLS: PropTypes.func.isRequired,
  discountCodeDetails: PropTypes.object,
  fetchActivationCodeDetails: PropTypes.func.isRequired,
  fetchPromoCodeDetails: PropTypes.func.isRequired,
  fetchReferral: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  isRecreationalCard: PropTypes.bool,
  isRenewal: PropTypes.bool,
  isTrialMembershipsActive: PropTypes.bool,
  membership: MembershipType,
  selectedMembershipType: PropTypes.string,
  setActivationCode: PropTypes.func.isRequired,
  setGCLid: PropTypes.func.isRequired,
  setPromoCode: PropTypes.func.isRequired,
  setSelectedMembershipType: PropTypes.func.isRequired,
  setTestVariant: PropTypes.func.isRequired,
  setTrialMembershipType: PropTypes.func.isRequired,
  setValidatedPromoMembershipType: PropTypes.func.isRequired,
  validatedPromoMembershipType: PropTypes.string,
}

function MarketingCodeProvider({
  children,
  clearDiscountCodeStateAndLS,
  discountCodeDetails,
  fetchActivationCodeDetails,
  fetchPromoCodeDetails,
  fetchReferral,
  flashErrorMessage,
  isRecreationalCard,
  isRenewal = false,
  isTrialMembershipsActive,
  membership,
  selectedMembershipType,
  setActivationCode,
  setGCLid,
  setPromoCode,
  setSelectedMembershipType,
  setTestVariant,
  setTrialMembershipType,
  setValidatedPromoMembershipType,
  validatedPromoMembershipType,
}) {
  const history = useHistory()
  const dispatch = useDispatch()
  const isReferralEnabled = useReferralEnabled()

  // Default to `NEW_MEMBER` when no membership is loaded so downstream promo
  // fetch/validation always have a usable status.
  const membershipStatus = membership?.membership_status__c || NEW_MEMBER

  // The membership the promo applies to: the existing card for current
  // members, or the one being purchased for new members.
  const membershipType =
    membershipStatus !== NEW_MEMBER
      ? membership?.membership_type__c
      : selectedMembershipType

  const { activationCode, promoCode, gclid, testVariant, referralId } =
    useTrackingParams()

  const handleError = useCallback(
    (_error, customMessage) => {
      // clear any saved codes if an invalid code is provided
      clearDiscountCodeStateAndLS()
      flashErrorMessage(customMessage || apiErrorToErrorMessage(_error))
      history.replace(history.location.pathname)
    },
    [clearDiscountCodeStateAndLS, flashErrorMessage, history],
  )

  const applyActivationCode = useCallback(async () => {
    try {
      await fetchActivationCodeDetails(activationCode)
    } catch ({ errors: { _error } }) {
      handleError(_error)
    }
  }, [activationCode, fetchActivationCodeDetails, handleError])

  useEffect(() => {
    if (!activationCode || !discountCodeDetails || !membershipStatus) return

    try {
      validateDiscountCode({
        discountCodeDetails,
        membershipStatus,
        isRenewal,
        membershipType: membership?.membership_type__c,
        selectedMembershipType,
      })

      setActivationCode(activationCode)

      const { autoRenewRequired } = discountCodeDetails

      if (autoRenewRequired) {
        dispatch(change('PaymentForm', 'autoRenewalAgreement', true))
      }
    } catch ({ errors: { _error } }) {
      handleError(_error)
    }
  }, [
    activationCode,
    discountCodeDetails,
    dispatch,
    handleError,
    isRenewal,
    membership,
    membershipStatus,
    selectedMembershipType,
    setActivationCode,
  ])

  const applyPromoCode = useCallback(async () => {
    // Remember the card we're validating against so we only re-check the
    // promo when the member actually switches to a different card.
    setValidatedPromoMembershipType(membershipType)

    try {
      const promoResponse = await fetchPromoCodeDetails({
        promoCode,
        isRenewal,
        membershipStatus,
        membershipType,
      })

      setPromoCode(promoCode)

      const { shouldAutoRenew, trialMembershipName, trialMembershipType } =
        processPromoCodeResponse(promoResponse, isTrialMembershipsActive)

      if (shouldAutoRenew) {
        dispatch(change('TermsAndConditionsForm', 'autoRenewalAgreement', true))
      }

      if (trialMembershipName) {
        setSelectedMembershipType(trialMembershipName)
        // Updates the validated membership type to the trial membership type
        setValidatedPromoMembershipType(trialMembershipName)
      }

      if (trialMembershipType) {
        setTrialMembershipType(trialMembershipType)
      }
    } catch (error) {
      const _error = error?.errors?._error
      // For Invalid Card Type and Invalid Flow Type, use the backend response 
      // message. All other errors use our standard message.
      const useResponseMessage =
        _error === INVALID_CARD_TYPE || _error === INVALID_FLOW_TYPE
      handleError(
        _error,
        useResponseMessage ? error?.response?.message : undefined,
      )
    }
  }, [
    promoCode,
    isRenewal,
    membershipStatus,
    membershipType,
    fetchPromoCodeDetails,
    handleError,
    isTrialMembershipsActive,
    dispatch,
    setPromoCode,
    setSelectedMembershipType,
    setTrialMembershipType,
    setValidatedPromoMembershipType,
  ])

  const applyReferralInfo = useCallback(async () => {
    try {
      fetchReferral(referralId)
    } catch ({ errors: { _error } }) {
      handleError(_error)
    }
  }, [referralId, fetchReferral, handleError])

  const appliedDiscountCodeName = discountCodeDetails?.name

  useEffect(() => {
    const currentCode = activationCode || promoCode
    if (appliedDiscountCodeName && appliedDiscountCodeName === currentCode)
      return

    if (activationCode && isRecreationalCard) {
      applyActivationCode()
    } else if (promoCode) {
      applyPromoCode()
    } else {
      clearDiscountCodeStateAndLS()
    }
  }, [
    activationCode,
    promoCode,
    applyActivationCode,
    applyPromoCode,
    clearDiscountCodeStateAndLS,
    appliedDiscountCodeName,
    isRecreationalCard,
  ])

  // If the member changes card type after a promo is applied, re-check it with
  // the backend so it only stays applied on a card the promo allows.
  useEffect(() => {
    if (
      shouldRevalidatePromoCode({
        promoCode,
        appliedDiscountCodeName,
        membershipType,
        lastValidatedMembershipType: validatedPromoMembershipType,
      })
    ) {
      applyPromoCode()
    }
  }, [
    promoCode,
    appliedDiscountCodeName,
    membershipType,
    validatedPromoMembershipType,
    applyPromoCode,
  ])

  useEffect(() => {
    if (gclid) setGCLid(gclid)
    if (testVariant) setTestVariant(testVariant)
  }, [gclid, setGCLid, testVariant, setTestVariant])

  useEffect(() => {
    if (referralId && isReferralEnabled) applyReferralInfo()
  }, [applyReferralInfo, referralId, isReferralEnabled])

  return children
}

MarketingCodeProvider.propTypes = propTypes

function mapStateToProps(state) {
  return {
    discountCodeDetails: memberSelectors.discountCodeDetails(state),
    isRecreationalCard: memberSelectors.isRecreationalCard(state),
    isTrialMembershipsActive: memberSelectors.isTrialMembershipsActive(state),
    membership: memberSelectors.membership(state),
    selectedMembershipType: memberSelectors.selectedMembershipType(state),
    validatedPromoMembershipType:
      memberSelectors.validatedPromoMembershipType(state),
  }
}

const mapDispatchToProps = {
  clearDiscountCodeStateAndLS: memberActions.clearDiscountCodeStateAndLS,
  fetchActivationCodeDetails: apiActions.fetchActivationCodeDetails,
  fetchPromoCodeDetails: apiActions.fetchPromoCodeDetails,
  fetchReferral: apiActions.fetchReferral,
  flashErrorMessage: flashErrorMessage,
  setActivationCode: memberActions.setActivationCode,
  setGCLid: memberActions.setGCLid,
  setPromoCode: memberActions.setPromoCode,
  setSelectedMembershipType: memberActions.setSelectedMembershipType,
  setTestVariant: memberActions.setTestVariant,
  setTrialMembershipType: memberActions.setTrialMembershipType,
  setValidatedPromoMembershipType:
    memberActions.setValidatedPromoMembershipType,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  MarketingCodeProvider,
)
